// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, MessageBar, MessageBarActions, MessageBarBody, tokens } from '@fluentui/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { removeAlert } from '../../redux/features/app/appSlice';
import { Dismiss16 } from './BundledIcons';

const useClasses = makeStyles({
    alert: {
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground6,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        maxWidth: '100%', // Ensure the alert does not exceed the container width
        overflow: 'auto',
        wordWrap: 'break-word', // Ensure long words break to the next line
        display: 'flex',
        justifyContent: 'space-between', // Ensure the close button is aligned to the right
        alignItems: 'center', // Vertically center the content
    },
    actionItems: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalMNudge,
    },
    button: {
        alignSelf: 'center',
    },
    alertContainer: {
        maxWidth: '100%', // Ensure the container does not exceed the window width
        overflow: 'hidden', // Hide overflow content
        padding: tokens.spacingHorizontalM, // Add some padding for better appearance
    },
});

export const Alerts: React.FC = () => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const { alerts } = useAppSelector((state: RootState) => state.app);

    const { t } = useTranslation();

    return (
        <div className={classes.alertContainer}>
            {alerts.map(({ type, message, onRetry }, index) => {
                return (
                    <MessageBar intent={type} key={`${index.toString()}-${type.toString()}`} className={classes.alert}>
                        <MessageBarBody>{message.slice(0, 1000) + (message.length > 1000 ? '...' : '')}</MessageBarBody>
                        <MessageBarActions>
                            <div className={classes.actionItems}>
                                {onRetry && <div onClick={onRetry}>Retry</div>}
                                <Dismiss16
                                    aria-label={t('alerts.dismissMessageAriaLabel')}
                                    onClick={() => {
                                        dispatch(removeAlert(index));
                                    }}
                                    color="black"
                                    className={classes.button}
                                />
                            </div>
                        </MessageBarActions>
                    </MessageBar>
                );
            })}
        </div>
    );
};
